// Procedural surface textures (Canvas2D): wood, parquet, tiles, marble, flagstones, carpets, grass
// and glowing floor decals. Generated once at start-up and cached; seeded so every client sees the
// same Ministry. Kept at 512–1024 px so integrated GPUs stay comfortable.
import * as THREE from 'three';

const cache = new Map<string, THREE.Texture>();

function rng(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return (s >>> 0) / 4294967296;
  };
}

function canvas(w: number, h = w): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d')!];
}

function finish(c: HTMLCanvasElement, repeat: [number, number] | null, color = true): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  if (color) t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 16;
  if (repeat) {
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(repeat[0], repeat[1]);
  }
  return t;
}

function cached<T extends THREE.Texture>(key: string, make: () => T): T {
  let t = cache.get(key) as T | undefined;
  if (!t) {
    t = make();
    cache.set(key, t);
  }
  return t;
}

/** Soft value-noise overlay: a tiny random grid scaled up with smoothing, drawn in several octaves. */
function noise(g: CanvasRenderingContext2D, w: number, h: number, r: () => number, alpha: number, cells = 16, dark = true): void {
  for (const oct of [1, 2, 4]) {
    const [n, ng] = canvas(cells * oct, cells * oct);
    const img = ng.createImageData(n.width, n.height);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = r() * 255;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = dark ? 0 : 255;
      img.data[i + 3] = v;
    }
    ng.putImageData(img, 0, 0);
    g.save();
    g.globalAlpha = alpha / oct;
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    g.drawImage(n, 0, 0, w, h);
    g.restore();
  }
}

function shade(hex: string, amt: number): string {
  const c = new THREE.Color(hex);
  if (amt >= 0) c.lerp(new THREE.Color('#ffffff'), amt);
  else c.lerp(new THREE.Color('#000000'), -amt);
  return `#${c.getHexString()}`;
}

// ─── Wood ────────────────────────────────────────────────────────────────────

function grain(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, base: string, r: () => number, horizontal = true, amt = 1): void {
  g.save();
  g.beginPath();
  g.rect(x, y, w, h);
  g.clip();
  g.fillStyle = base;
  g.fillRect(x, y, w, h);
  const len = horizontal ? w : h;
  const span = horizontal ? h : w;
  const lines = Math.max(6, Math.floor(span / 3));
  for (let i = 0; i < lines; i++) {
    const o = (i / lines) * span + r() * 2;
    const amp = 1 + r() * 3;
    const freq = 0.004 + r() * 0.01;
    const ph = r() * 10;
    g.beginPath();
    for (let t = 0; t <= len; t += 8) {
      const d = o + Math.sin(t * freq + ph) * amp + Math.sin(t * freq * 3.1 + ph) * amp * 0.3;
      if (horizontal) g.lineTo(x + t, y + d);
      else g.lineTo(x + d, y + t);
    }
    g.strokeStyle = r() < 0.5 ? `rgba(20,10,4,${(0.08 + r() * 0.14) * amt})` : `rgba(255,230,190,${(0.03 + r() * 0.05) * amt})`;
    g.lineWidth = 0.6 + r() * 1.4;
    g.stroke();
  }
  // A knot now and then.
  if (r() < 0.35 * amt) {
    const kx = horizontal ? x + r() * w : x + w / 2;
    const ky = horizontal ? y + h / 2 : y + r() * h;
    for (let k = 4; k > 0; k--) {
      g.beginPath();
      g.ellipse(kx, ky, k * (horizontal ? 5 : 2.2), k * (horizontal ? 2.2 : 5), 0, 0, Math.PI * 2);
      g.strokeStyle = `rgba(30,14,6,${0.12 + 0.05 * k})`;
      g.lineWidth = 1;
      g.stroke();
    }
  }
  g.restore();
}

/** Long planks, e.g. the table top or a shop floor. */
export function planks(base = '#5a3522', key = 'planks', repeat: [number, number] = [1, 1], rows = 8, contrast = 0.22): THREE.CanvasTexture {
  return cached(`${key}:${base}:${rows}:${contrast}`, () => {
    const S = 1024;
    const [c, g] = canvas(S);
    const r = rng(11);
    const ph = S / rows;
    for (let i = 0; i < rows; i++) {
      let x = -r() * S * 0.5;
      while (x < S) {
        const len = S * (contrast < 0.1 ? 0.9 + r() * 0.8 : 0.45 + r() * 0.5);
        grain(g, x, i * ph, len, ph, shade(base, (r() - 0.5) * contrast), r, true, contrast < 0.1 ? 0.35 : 1);
        g.fillStyle = `rgba(15,8,3,${contrast < 0.1 ? 0.14 : 0.55})`;
        g.fillRect(x, i * ph, 2, ph);
        x += len;
      }
      g.fillStyle = `rgba(15,8,3,${contrast < 0.1 ? 0.18 : 0.5})`;
      g.fillRect(0, i * ph, S, 2);
    }
    noise(g, S, S, r, contrast < 0.1 ? 0.05 : 0.08);
    return finish(c, repeat);
  });
}

export function herringbone(base = '#8a5a34', repeat: [number, number] = [1, 1]): THREE.CanvasTexture {
  return cached(`herring:${base}`, () => {
    const S = 1024;
    const [c, g] = canvas(S);
    const r = rng(23);
    g.fillStyle = shade(base, -0.22);
    g.fillRect(0, 0, S, S);
    const bw = 32;
    const bl = bw * 4;
    for (let row = -8; row < 16; row++) {
      for (let col = -2; col < 12; col++) {
        const x0 = col * bl;
        const y0 = row * bw * 2 + col * bw * 2;
        for (const flip of [0, 1]) {
          g.save();
          g.translate(x0 + (flip ? bw * 2 : 0), y0 + (flip ? bw * 2 : 0));
          g.rotate(flip ? -Math.PI / 4 : Math.PI / 4);
          grain(g, 0, 0, bl * 0.72, bw * 0.72 - 1, shade(base, (r() - 0.5) * 0.1), r, true, 0.45);
          g.restore();
        }
      }
    }
    noise(g, S, S, r, 0.035);
    return finish(c, repeat);
  });
}

// ─── Stone, tiles, marble ────────────────────────────────────────────────────

export function tiles(a: string, b: string, n = 8, key = 'tiles', repeat: [number, number] = [1, 1], diamond = false): THREE.CanvasTexture {
  return cached(`${key}:${a}:${b}:${n}:${diamond}`, () => {
    const S = 1024;
    const [c, g] = canvas(S);
    const r = rng(37);
    const t = S / n;
    // Quiet by design: thin, soft grout, barely-there bevels and little per-tile variation, so a
    // floor reads as one surface in its room's colour rather than as a pattern.
    g.fillStyle = shade(a, -0.3);
    g.fillRect(0, 0, S, S);
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        const col = (i + j) % 2 ? a : b;
        const x = i * t;
        const y = j * t;
        const v = (r() - 0.5) * 0.04;
        const grad = g.createLinearGradient(x, y, x + t, y + t);
        grad.addColorStop(0, shade(col, 0.03 + v));
        grad.addColorStop(1, shade(col, -0.03 + v));
        g.fillStyle = grad;
        g.fillRect(x + 1.5, y + 1.5, t - 3, t - 3);
        if (diamond) {
          g.save();
          g.translate(x + t / 2, y + t / 2);
          g.rotate(Math.PI / 4);
          g.fillStyle = shade((i + j) % 2 ? b : a, 0.02);
          g.globalAlpha = 0.7;
          g.fillRect(-t * 0.14, -t * 0.14, t * 0.28, t * 0.28);
          g.restore();
        }
      }
    }
    noise(g, S, S, r, 0.04, 32);
    return finish(c, repeat);
  });
}

export function marble(base = '#e6e0d4', vein = '#9a8f80', repeat: [number, number] = [1, 1], n = 4): THREE.CanvasTexture {
  return cached(`marble:${base}:${vein}:${n}`, () => {
    const S = 1024;
    const [c, g] = canvas(S);
    const r = rng(41);
    const t = S / n;
    g.fillStyle = shade(base, -0.22);
    g.fillRect(0, 0, S, S);
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        const x = i * t;
        const y = j * t;
        g.save();
        g.beginPath();
        g.rect(x + 2, y + 2, t - 4, t - 4);
        g.clip();
        g.fillStyle = shade(base, (r() - 0.5) * 0.04);
        g.fillRect(x, y, t, t);
        for (let v = 0; v < 5; v++) {
          g.beginPath();
          let px = x + r() * t;
          let py = y;
          g.moveTo(px, py);
          while (py < y + t) {
            px += (r() - 0.5) * 30;
            py += 10 + r() * 20;
            g.lineTo(px, py);
          }
          g.strokeStyle = vein;
          g.globalAlpha = 0.05 + r() * 0.1;
          g.lineWidth = 0.6 + r() * 1.4;
          g.stroke();
        }
        g.restore();
      }
    }
    noise(g, S, S, r, 0.03, 32);
    return finish(c, repeat);
  });
}

/** Irregular flagstones in running courses (halls, the lobby plaza). */
export function flagstones(base = '#9d9383', repeat: [number, number] = [1, 1]): THREE.CanvasTexture {
  return cached(`flag:${base}`, () => {
    const S = 1024;
    const [c, g] = canvas(S);
    const r = rng(53);
    g.fillStyle = shade(base, -0.3);
    g.fillRect(0, 0, S, S);
    const rows = 8;
    const rh = S / rows;
    for (let i = 0; i < rows; i++) {
      let x = -r() * 80;
      while (x < S) {
        const w = 90 + r() * 120;
        const col = shade(base, (r() - 0.5) * 0.1);
        const grad = g.createLinearGradient(x, i * rh, x, i * rh + rh);
        grad.addColorStop(0, shade(col, 0.04));
        grad.addColorStop(1, shade(col, -0.04));
        g.fillStyle = grad;
        g.beginPath();
        g.roundRect(x + 3, i * rh + 3, w - 6, rh - 6, 10);
        g.fill();
        // wrap the course so the texture tiles horizontally
        if (x + w > S) {
          g.beginPath();
          g.roundRect(x + 3 - S, i * rh + 3, w - 6, rh - 6, 10);
          g.fill();
        }
        x += w;
      }
    }
    noise(g, S, S, r, 0.06, 32);
    return finish(c, repeat);
  });
}

export function grass(repeat: [number, number] = [1, 1]): THREE.CanvasTexture {
  return cached('grass', () => {
    const S = 512;
    const [c, g] = canvas(S);
    const r = rng(61);
    g.fillStyle = '#3f5f2e';
    g.fillRect(0, 0, S, S);
    noise(g, S, S, r, 0.22, 8);
    for (let i = 0; i < 3000; i++) {
      g.fillStyle = r() < 0.5 ? `rgba(120,160,70,${0.1 + r() * 0.18})` : `rgba(20,40,15,${0.1 + r() * 0.18})`;
      const x = r() * S;
      const y = r() * S;
      g.fillRect(x, y, 1.5, 3 + r() * 3);
    }
    return finish(c, repeat);
  });
}

// ─── Carpets and decals ──────────────────────────────────────────────────────

/** A rug with borders, a field pattern and a medallion. Aspect follows w:h (in texture units). */
export function carpet(field: string, border: string, w = 3, h = 2, key = 'rug', medallion = true): THREE.CanvasTexture {
  return cached(`carpet:${key}:${field}:${border}:${w}:${h}:${medallion}`, () => {
    const W = 1024;
    const H = Math.round((W * h) / w);
    const [c, g] = canvas(W, H);
    const r = rng(71);
    g.fillStyle = border;
    g.fillRect(0, 0, W, H);
    const b = Math.min(W, H) * 0.08;
    g.fillStyle = shade(border, 0.35);
    g.fillRect(b * 0.35, b * 0.35, W - b * 0.7, H - b * 0.7);
    g.fillStyle = border;
    g.fillRect(b * 0.5, b * 0.5, W - b, H - b);
    // border motif: small diamonds
    g.fillStyle = shade(border, 0.45);
    const step = b * 0.9;
    for (let x = b; x < W - b; x += step) {
      for (const y of [b * 0.75, H - b * 0.75]) diamond(g, x, y, b * 0.18);
    }
    for (let y = b; y < H - b; y += step) {
      for (const x of [b * 0.75, W - b * 0.75]) diamond(g, x, y, b * 0.18);
    }
    g.fillStyle = field;
    g.fillRect(b * 1.3, b * 1.3, W - b * 2.6, H - b * 2.6);
    // field lattice
    g.strokeStyle = shade(field, -0.25);
    g.lineWidth = 3;
    const cell = b * 1.6;
    for (let x = b * 1.3; x < W - b * 1.3; x += cell) {
      for (let y = b * 1.3; y < H - b * 1.3; y += cell) {
        g.beginPath();
        g.moveTo(x + cell / 2, y);
        g.lineTo(x + cell, y + cell / 2);
        g.lineTo(x + cell / 2, y + cell);
        g.lineTo(x, y + cell / 2);
        g.closePath();
        g.stroke();
      }
    }
    if (medallion) {
      const m = Math.min(W, H) * 0.22;
      g.fillStyle = shade(border, 0.1);
      g.beginPath();
      g.ellipse(W / 2, H / 2, m * 1.35, m, 0, 0, Math.PI * 2);
      g.fill();
      g.strokeStyle = shade(border, 0.5);
      g.lineWidth = 6;
      g.stroke();
      g.fillStyle = shade(field, 0.2);
      g.beginPath();
      g.ellipse(W / 2, H / 2, m * 0.8, m * 0.55, 0, 0, Math.PI * 2);
      g.fill();
    }
    noise(g, W, H, r, 0.12, 32);
    return finish(c, null);
  });
}

function diamond(g: CanvasRenderingContext2D, x: number, y: number, s: number): void {
  g.beginPath();
  g.moveTo(x, y - s);
  g.lineTo(x + s, y);
  g.lineTo(x, y + s);
  g.lineTo(x - s, y);
  g.closePath();
  g.fill();
}

/** Additive glowing summoning circle (transparent background). */
export function magicCircle(color: string, key = 'circle', points = 5): THREE.CanvasTexture {
  return cached(`magic:${key}:${color}`, () => {
    const S = 1024;
    const [c, g] = canvas(S);
    const cx = S / 2;
    g.strokeStyle = color;
    g.fillStyle = color;
    g.shadowColor = color;
    g.shadowBlur = 18;
    const ring = (rad: number, w: number) => {
      g.beginPath();
      g.arc(cx, cx, rad, 0, Math.PI * 2);
      g.lineWidth = w;
      g.stroke();
    };
    ring(S * 0.47, 10);
    ring(S * 0.43, 4);
    ring(S * 0.3, 6);
    // rune ticks between the outer rings
    g.font = `bold ${S * 0.035}px serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    const runes = 'ᚠᚢᚦᚨᚱᚲᚷᚹᚺᚾᛁᛃᛇᛈᛉᛊᛏᛒᛖᛗᛚᛜᛞᛟ';
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      g.save();
      g.translate(cx + Math.cos(a) * S * 0.45, cx + Math.sin(a) * S * 0.45);
      g.rotate(a + Math.PI / 2);
      g.fillText(runes[i % runes.length]!, 0, 0);
      g.restore();
    }
    // star polygon
    g.beginPath();
    for (let i = 0; i <= points; i++) {
      const a = -Math.PI / 2 + ((i * 2) % points) * ((Math.PI * 2) / points);
      const x = cx + Math.cos(a) * S * 0.43;
      const y = cx + Math.sin(a) * S * 0.43;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.lineWidth = 5;
    g.stroke();
    for (let i = 0; i < points; i++) {
      const a = -Math.PI / 2 + (i * Math.PI * 2) / points;
      g.beginPath();
      g.arc(cx + Math.cos(a) * S * 0.43, cx + Math.sin(a) * S * 0.43, S * 0.03, 0, Math.PI * 2);
      g.lineWidth = 4;
      g.stroke();
    }
    return finish(c, null);
  });
}

/** A star chart for Divination: constellations and a zodiac ring. */
export function starChart(color = '#bff5dd'): THREE.CanvasTexture {
  return cached(`stars:${color}`, () => {
    const S = 1024;
    const [c, g] = canvas(S);
    const r = rng(83);
    const cx = S / 2;
    g.strokeStyle = color;
    g.fillStyle = color;
    g.shadowColor = color;
    g.shadowBlur = 12;
    for (const [rad, w] of [
      [0.48, 6],
      [0.44, 2],
      [0.26, 3],
    ] as const) {
      g.beginPath();
      g.arc(cx, cx, S * rad, 0, Math.PI * 2);
      g.lineWidth = w;
      g.stroke();
    }
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      g.beginPath();
      g.moveTo(cx + Math.cos(a) * S * 0.44, cx + Math.sin(a) * S * 0.44);
      g.lineTo(cx + Math.cos(a) * S * 0.48, cx + Math.sin(a) * S * 0.48);
      g.lineWidth = 3;
      g.stroke();
    }
    for (let k = 0; k < 7; k++) {
      const a0 = r() * Math.PI * 2;
      const d0 = S * (0.1 + r() * 0.3);
      let px = cx + Math.cos(a0) * d0;
      let py = cx + Math.sin(a0) * d0;
      g.beginPath();
      g.moveTo(px, py);
      for (let s = 0; s < 4; s++) {
        g.arc(px, py, 5, 0, Math.PI * 2);
        px += (r() - 0.5) * 120;
        py += (r() - 0.5) * 120;
        g.lineTo(px, py);
      }
      g.lineWidth = 1.5;
      g.stroke();
    }
    for (let i = 0; i < 160; i++) {
      g.globalAlpha = 0.3 + r() * 0.7;
      g.beginPath();
      const a = r() * Math.PI * 2;
      const d = r() * S * 0.43;
      g.arc(cx + Math.cos(a) * d, cx + Math.sin(a) * d, 1 + r() * 2.5, 0, Math.PI * 2);
      g.fill();
    }
    return finish(c, null);
  });
}

/** Soft radial blob for contact shadows and light pools. */
export function radial(inner = 'rgba(0,0,0,0.55)', key = 'shadow'): THREE.CanvasTexture {
  return cached(`radial:${key}`, () => {
    const S = 256;
    const [c, g] = canvas(S);
    const grad = g.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    grad.addColorStop(0, inner);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, S, S);
    return finish(c, null);
  });
}

/** A soft pool of light: white, falling off smoothly to nothing (tinted per use, drawn additively). */
export function glowPool(): THREE.CanvasTexture {
  return cached('glow-pool', () => {
    const S = 256;
    const [c, g] = canvas(S);
    const grad = g.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    for (let i = 0; i <= 8; i++) {
      const t = i / 8;
      grad.addColorStop(t, `rgba(255,255,255,${(Math.pow(1 - t, 2.2) * (1 - 0.15 * t)).toFixed(3)})`);
    }
    g.fillStyle = grad;
    g.fillRect(0, 0, S, S);
    return finish(c, null);
  });
}

export function disposeTextures(): void {
  for (const t of cache.values()) t.dispose();
  cache.clear();
}

/** Dressed stone blocks in running courses (walls). */
export function ashlar(base = '#9b938a', repeat: [number, number] = [1, 1]): THREE.CanvasTexture {
  return cached(`ashlar:${base}`, () => {
    const S = 512;
    const [c, g] = canvas(S);
    const r = rng(97);
    g.fillStyle = shade(base, -0.45);
    g.fillRect(0, 0, S, S);
    const rows = 8;
    const rh = S / rows;
    for (let i = 0; i < rows; i++) {
      let x = i % 2 ? -rh : 0;
      while (x < S) {
        const w = rh * (1.4 + r() * 1.2);
        const col = shade(base, (r() - 0.5) * 0.16);
        g.fillStyle = col;
        g.fillRect(x + 2, i * rh + 2, w - 4, rh - 4);
        // top-left light edge, bottom-right shade: reads as chiselled under toon light
        g.fillStyle = 'rgba(255,255,255,0.08)';
        g.fillRect(x + 2, i * rh + 2, w - 4, 3);
        g.fillStyle = 'rgba(0,0,0,0.12)';
        g.fillRect(x + 2, i * rh + rh - 5, w - 4, 3);
        if (x + w > S) {
          g.fillStyle = col;
          g.fillRect(x + 2 - S, i * rh + 2, w - 4, rh - 4);
        }
        x += w;
      }
    }
    noise(g, S, S, r, 0.12, 32);
    return finish(c, repeat);
  });
}
